import GridViewIcon from "@mui/icons-material/GridView";
import LockIcon from "@mui/icons-material/Lock";
import WidgetsIcon from "@mui/icons-material/Widgets";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useEventContainer } from "../../../containers/EventContainer";
import { useIdentity } from "../../../containers/IdentityContainer";
import { useActivation, ACTIVATION_EVENTS, SPOTLIGHT_IDS, SpotlightPulse } from "../../../components/Activation";
import { useTowingEquipmentState } from "../../../containers/TowingEquipmentContainer";
import { useUserPreferences } from "../../../containers/UserPreferencesContainer";
import { TowingEquipment } from "../../../hooks/useTowingEquipment";
import { Hangar, ParamSet } from "../../../types";
import mixpanel from "../../../utils/mixpanel";
import { getSOPsForLocation } from "../../../utils/sopOverrides";
import { getDefaultTugForLocation } from "../../../utils/towingEquipment";
import { HOLDING_AREA_ID } from "../../MultiHangar/HoldingArea";
import { useLayout } from "../../Ramp/CollapsibleRampActionCard";
import {
  CustomPlacementOptions,
  CustomStackDialog,
} from "../CustomStackDialog";
import { EvaluatingStackDialog } from "../EvaluatingStackDialog";
import {
  StackStrategy,
  generateDevParamSets,
  makeParamSetForType,
} from "../GenerateParamSets";
import { HangarStack } from "../Hangar";
import { SelectStackStrategy } from "./SelectStackStrategy";
import { SelectTowingEquipment } from "./SelectTowingEquipment";
import { runAllParamSets } from "./run";

const ButtonAutoStack: React.FC<{
  disabled: boolean;
  onClick: () => void;
  icon: boolean;
}> = ({ disabled, onClick, icon }) => {
  const { t } = useTranslation();
  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick();
    },
    [onClick]
  );

  if (icon) {
    return (
      <Tooltip title={t("screens.hangar.runStack.tooltips.autoStack")} arrow>
        <IconButton disabled={disabled} onClick={handleClick} color="success">
          <WidgetsIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      disabled={disabled}
      fullWidth
      variant="contained"
      color="success"
      endIcon={<WidgetsIcon />}
      onClick={handleClick}
    >
      {t("screens.hangar.runStack.buttons.autoStack")}
    </Button>
  );
};

const ButtonResults: React.FC<{
  disabled: boolean;
  onClick: () => void;
  icon: boolean;
}> = ({ disabled, onClick, icon }) => {
  const { t } = useTranslation();
  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick();
    },
    [onClick]
  );

  if (icon) {
    return (
      <Tooltip title={t("screens.hangar.runStack.tooltips.results")} arrow>
        <IconButton disabled={disabled} onClick={handleClick} color="primary">
          <GridViewIcon />
        </IconButton>
      </Tooltip>
    );
  }
  return (
    <Button
      fullWidth
      disabled={disabled}
      variant="contained"
      color="primary"
      endIcon={<GridViewIcon />}
      onClick={handleClick}
    >
      {t("screens.hangar.runStack.buttons.results")}
    </Button>
  );
};

type Props = {
  hangar: Hangar;
  stacks: HangarStack[];
  setStacks: (stacks: HangarStack[]) => void;
  paramSet: ParamSet;
  isAlgorithmTesting?: boolean;
  customPlacementOptions?: CustomPlacementOptions;
  stackStrategy: StackStrategy;
  setStackStrategy: (s: StackStrategy) => void;
  disabled?: boolean;
  isScheduledView?: boolean;
};

export enum Status {
  IN_PROGRESS = "in progress",
  COMPLETE = "complete",
}

export const RunStack: React.FC<Props> = ({
  hangar,
  stacks,
  setStacks,
  paramSet,
  customPlacementOptions,
  isAlgorithmTesting = false,
  stackStrategy,
  setStackStrategy,
  disabled = false,
  isScheduledView = false,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const { preferences, setPreferences } = useUserPreferences();
  const { towingEquipments: allTowingEquipments } = useTowingEquipmentState();
  const { isDrawerOpen } = useLayout();
  const { airplxIdentity, airplxToken } = useIdentity();
  const { recordAutoStackRun } = useActivation();
  const [open, setOpen] = React.useState<boolean>(false);
  const [status, setStatus] = React.useState<Status>();

  // Track AutoStack completion for activation
  React.useEffect(() => {
    if (status === Status.COMPLETE) {
      recordAutoStackRun();
    }
  }, [status, recordAutoStackRun]);
  const [ignoreTowingEquipment, setIgnoreTowingEquipment] =
    React.useState<boolean>(false);
  const towingEquipments = allTowingEquipments.filter((t) => t.in_use);
  const [stacksProgress, setStacksProgress] = React.useState<number[]>([]);
  const isOpeningRef = React.useRef(false);

  const [selectedTowingEquipment, setSelectedTowingEquipment] = React.useState<
    TowingEquipment | undefined
  >(() =>
    getDefaultTugForLocation(hangar.towing_equipment_id, towingEquipments)
  );
  const [showCustomDialog, setShowCustomDialog] =
    React.useState<boolean>(false);

  const handleOpenDialog = React.useCallback(() => {
    if (isOpeningRef.current || open) {
      return;
    }
    isOpeningRef.current = true;

    // Set default towing equipment when dialog opens
    const defaultTug = getDefaultTugForLocation(
      hangar.towing_equipment_id,
      towingEquipments
    );
    setSelectedTowingEquipment(defaultTug);

    setOpen(true);
    // Reset the ref after a short delay
    setTimeout(() => {
      isOpeningRef.current = false;
    }, 100);
  }, [open, hangar.towing_equipment_id, towingEquipments]);

  // Listen for activation checklist event to open AutoStack dialog
  const { addEventListener, removeEventListener } = useEventContainer();
  React.useEffect(() => {
    const handleRunAutostack = () => {
      handleOpenDialog();
    };
    addEventListener(ACTIVATION_EVENTS.RUN_AUTOSTACK, handleRunAutostack);
    return () => {
      removeEventListener(ACTIVATION_EVENTS.RUN_AUTOSTACK, handleRunAutostack);
    };
  }, [addEventListener, removeEventListener, handleOpenDialog]);

  const onClickRunStack = React.useCallback(
    async (customPlacementOptions?: CustomPlacementOptions) => {
      mixpanel.track("Clicked Autostack", {
        location_name: hangar.name,
        location_type: "hangar",
        stack_strategy: stackStrategy.toString(),
      });
      window.globalThis.airplxKillSwitch = 0;
      window.globalThis.airplxAborter = new AbortController();
      setStacksProgress([]);
      setStatus(Status.IN_PROGRESS);

      const baseParamSet: ParamSet = {
        ...paramSet,
        options: {
          ...paramSet.options,
          towing_equipment_id: ignoreTowingEquipment
            ? null
            : selectedTowingEquipment?.towing_equipment_id,
        },
      };
      const effectiveSOPs = getSOPsForLocation(activeFBO, hangar);

      const paramSetForType = makeParamSetForType(
        isAlgorithmTesting ? StackStrategy.dev : stackStrategy,
        baseParamSet,
        customPlacementOptions,
        effectiveSOPs
      );

      const paramsSets = generateDevParamSets(
        paramSetForType,
        hangar.stack.tenants.map(({ aircraft }) => aircraft),
        customPlacementOptions
      );

      await runAllParamSets(
        hangar,
        paramsSets,
        (stacks) => setStacks(stacks),
        (progress) => setStacksProgress(progress),
        airplxIdentity,
        airplxToken
      );

      if (
        !window.globalThis.airplxKillSwitch ||
        window.globalThis.airplxKillSwitch < 2
      ) {
        setStatus(Status.COMPLETE);
      }
    },
    [
      open,
      setStacks,
      paramSet,
      selectedTowingEquipment,
      ignoreTowingEquipment,
      customPlacementOptions,
    ]
  );

  return (
    <>
      <EvaluatingStackDialog
        open={Boolean(status)}
        hangarId={hangar.id}
        isAlgorithmTesting={isAlgorithmTesting}
        status={status}
        stacks={stacks}
        progress={stacksProgress}
        onClose={() => setStatus(null)}
      />
      <Stack
        direction="row"
        spacing={1}
        justifyContent={isDrawerOpen ? "space-between" : "space-around"}
        sx={{ m: 0, pl: 1, pr: 1 }}
      >
        {isAlgorithmTesting ? (
          <>
            <Button
              fullWidth
              variant="contained"
              color="success"
              endIcon={<WidgetsIcon />}
              onClick={() => {
                setOpen(false);
                onClickRunStack(customPlacementOptions);
              }}
            >
              {t("screens.hangar.runStack.buttons.autoStackDev")}
            </Button>
          </>
        ) : (
          <SpotlightPulse id={SPOTLIGHT_IDS.AUTOSTACK} fullWidth>
            <ButtonAutoStack
              disabled={
                hangar.autostacking_enabled === false ||
                activeFBO.subscription === "standard" ||
                hangar.id === HOLDING_AREA_ID ||
                disabled ||
                isScheduledView
              }
              onClick={handleOpenDialog}
              icon={!isDrawerOpen}
            />
          </SpotlightPulse>
        )}
        <Box sx={{ flex: 1, width: '100%' }}>
          <ButtonResults
            disabled={!stacks.length}
            onClick={() => setStatus(Status.COMPLETE)}
            icon={!isDrawerOpen}
          />
        </Box>
      </Stack>
      {showCustomDialog && (
        <CustomStackDialog
          onClickRunStack={(customPlacementOptions: CustomPlacementOptions) => {
            setOpen(false);
            onClickRunStack(customPlacementOptions);
            setShowCustomDialog(false);
          }}
          onClose={() => {
            setOpen(true);
            setShowCustomDialog(false);
            setStatus(null);
          }}
        />
      )}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setStatus(null);
        }}
        maxWidth="sm"
      >
        <DialogTitle>{t("screens.hangar.runStack.dialog.title")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {preferences.hideLockReminderAlert ? (
              <div />
            ) : (
              <Alert
                severity="warning"
                icon={<LockIcon fontSize="inherit" />}
                onClose={() => {
                  setPreferences({
                    ...preferences,
                    hideLockReminderAlert: true,
                  });
                }}
              >
                {t("screens.hangar.runStack.alerts.lockReminder")}
              </Alert>
            )}
            <SelectStackStrategy
              paramSet={paramSet}
              stackStrategy={stackStrategy}
              setStackStrategy={setStackStrategy}
              hangar={hangar}
            />
            <SelectTowingEquipment
              ignoreTowingEquipment={ignoreTowingEquipment}
              setIgnoreTowingEquipment={setIgnoreTowingEquipment}
              selectedTowingEquipment={selectedTowingEquipment}
              setSelectedTowingEquipment={setSelectedTowingEquipment}
              towingEquipments={towingEquipments}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("screens.hangar.runStack.buttons.cancel")}</Button>
          {stackStrategy === StackStrategy.custom ? (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                setOpen(false);
                setShowCustomDialog(true);
              }}
            >
              {t("screens.hangar.runStack.buttons.customizeStack")}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                setOpen(false);
                onClickRunStack();
              }}
            >
              {t("screens.hangar.runStack.buttons.runStack")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
